import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Rocket, CheckCircle, Play } from 'lucide-react';
import AnimatedSection from '../../components/AnimatedSection';

const QuickStart = () => {
  const steps = [
    {
      id: 'step-1',
      title: 'Download & Install',
      description: 'Download Metas for your platform and complete the installation',
      time: '2 minutes'
    },
    {
      id: 'step-2',
      title: 'Connect Your Exchange',
      description: 'Add your trading account from 25+ supported exchanges',
      time: '3 minutes'
    },
    {
      id: 'step-3',
      title: 'Configure Charts',
      description: 'Set up your first footprint chart with order flow analysis',
      time: '5 minutes'
    },
    {
      id: 'step-4',
      title: 'Start Trading',
      description: 'Begin analyzing markets with professional tools',
      time: '1 minute'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-[#0B0F1A] via-[#1a1147] to-[#0B0F1A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-[#00D4FF]/10" />
        
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Rocket className="mx-auto mb-6 text-[#7C3AED]" size={64} />
            <h1 className="text-6xl font-bold mb-6">Quick Start Guide</h1>
            <p className="text-2xl text-[#9CA3AF] max-w-3xl mx-auto">
              Get up and running with Metas in under 15 minutes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <AnimatedSection key={step.id} delay={index * 0.1}>
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#00D4FF] rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold">{step.title}</h3>
                      <span className="text-sm text-[#00D4FF] bg-[#00D4FF]/10 px-3 py-1 rounded-full">
                        {step.time}
                      </span>
                    </div>
                    <p className="text-[#9CA3AF] mb-4">{step.description}</p>
                    <button className="flex items-center gap-2 text-[#7C3AED] hover:text-white transition-colors">
                      <Play size={16} />
                      Watch Tutorial
                    </button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Video Tutorial */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <h2 className="text-4xl font-bold text-center mb-12">Video Walkthrough</h2>
            <div className="aspect-video bg-gradient-to-br from-[#1a1147] to-[#0B0F1A] rounded-2xl border border-white/10 flex items-center justify-center">
              <Play size={80} className="text-white/40" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <h2 className="text-4xl font-bold text-center mb-12">What's Next?</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { id: 'explore', title: 'Explore Features', description: 'Discover all the powerful tools available', path: '/features' },
              { id: 'join', title: 'Join Community', description: 'Connect with other traders and learn together', path: '/resources/community' },
              { id: 'upgrade', title: 'Upgrade Plan', description: 'Unlock premium features and advanced tools', path: '/pricing' }
            ].map((item, index) => (
              <AnimatedSection key={item.id} delay={index * 0.1}>
                <Link to={item.path}>
                  <motion.div 
                    className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8 text-center hover:border-[#7C3AED]/50 transition-all cursor-pointer h-full"
                    whileHover={{ scale: 1.05, translateY: -8 }}
                  >
                    <CheckCircle className="mx-auto mb-4 text-[#00D4FF]" size={40} />
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-[#9CA3AF]">{item.description}</p>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuickStart;